import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Database } from "@/integrations/supabase/types";
import { DiscoveryFilterValues, DEFAULT_FILTERS } from "@/components/discovery/DiscoveryFilters";

type DbProfile = Database["public"]["Tables"]["profiles"]["Row"];

export interface DiscoveryProfile {
  id: string;
  name: string;
  username?: string;
  age: number;
  bio: string;
  photos: string[];
  hasVideo: boolean;
  videoUrl?: string;
  isVerified: boolean;
  badges: string[];
  distance: string;
  distanceNum?: number;
  religion?: string;
  gender?: string;
  lastSeen?: string;
  socialStatus?: string;
  statusVisibility?: string;
  lifestyleVerified?: boolean;
  roleTitle?: string;
  countryCode?: string;
  cityId?: string;
}

export interface DiscoveryOptions {
  cityId?: string;
  countryCode?: string;
  teleportSessionId?: string;
  tabContext?: string;
  /** Callback when user performs an interaction (like, pop, match) */
  onInteraction?: (type: "like" | "pop" | "match") => void;
}

// Extended DB profile type to include joined data
type ExtendedDbProfile = DbProfile & {
  cities?: { country_code: string } | null;
};

const transformDbProfile = (
  dbProfile: ExtendedDbProfile,
  userLat?: number | null,
  userLng?: number | null
): DiscoveryProfile => {
  let distance = "Distance unavailable";
  let distanceNum: number | undefined;
  
  if (userLat && userLng && dbProfile.latitude && dbProfile.longitude) {
    // Haversine formula for accurate distance
    const R = 3959; // Earth's radius in miles
    const lat1 = (userLat * Math.PI) / 180;
    const lat2 = (dbProfile.latitude * Math.PI) / 180;
    const dLat = ((dbProfile.latitude - userLat) * Math.PI) / 180;
    const dLng = ((dbProfile.longitude - userLng) * Math.PI) / 180;
    
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    
    distanceNum = d;
    
    // Format distance with one decimal place
    if (d < 1) {
      distance = `${d.toFixed(1)} miles away`;
    } else {
      distance = `${d.toFixed(1)} miles away`;
    }
  }
  
  // Get country code from joined cities data
  const countryCode = dbProfile.cities?.country_code || undefined;
  
  return {
    id: dbProfile.id,
    name: dbProfile.name,
    username: dbProfile.username || undefined,
    age: dbProfile.age || 0,
    bio: dbProfile.bio || "",
    photos: dbProfile.photo_url ? [dbProfile.photo_url] : [],
    hasVideo: dbProfile.has_intro_video || false,
    videoUrl: dbProfile.video_url || undefined,
    isVerified: dbProfile.is_verified || false,
    badges: dbProfile.is_verified ? ["Verified"] : [],
    distance,
    distanceNum,
    religion: dbProfile.religion || undefined,
    gender: dbProfile.gender || undefined,
    lastSeen: dbProfile.last_seen || undefined,
    socialStatus: dbProfile.social_status || undefined,
    statusVisibility: dbProfile.status_visibility || undefined,
    lifestyleVerified: dbProfile.lifestyle_verified || false,
    roleTitle: dbProfile.role_title || undefined,
    countryCode,
    cityId: dbProfile.city_id || undefined,
    // New fields
    ...(((dbProfile as any).country) ? { country: (dbProfile as any).country } : {}),
    ...(((dbProfile as any).nationality) ? { nationality: (dbProfile as any).nationality } : {}),
    ...(((dbProfile as any).marital_status) ? { maritalStatus: (dbProfile as any).marital_status } : {}),
    ...(((dbProfile as any).education) ? { education: (dbProfile as any).education } : {}),
    ...(((dbProfile as any).profession) ? { profession: (dbProfile as any).profession } : {}),
    ...(((dbProfile as any).annual_income) ? { annualIncome: (dbProfile as any).annual_income } : {}),
    ...(((dbProfile as any).socioeconomic_status) ? { socioeconomicStatus: (dbProfile as any).socioeconomic_status } : {}),
    ...(((dbProfile as any).looking_for) ? { lookingFor: (dbProfile as any).looking_for } : {}),
    ...(((dbProfile as any).willing_to_relocate) ? { willingToRelocate: (dbProfile as any).willing_to_relocate } : {}),
    ...(((dbProfile as any).children) ? { children: (dbProfile as any).children } : {}),
  };
};

export const useProfileDiscovery = (options: DiscoveryOptions = {}) => {
  const { user, profile: userProfile, refreshProfile } = useAuthContext();
  const { cityId, countryCode, teleportSessionId, tabContext = "discovery", onInteraction } = options;

  const [profiles, setProfiles] = useState<DiscoveryProfile[]>([]);
  const [filteredProfiles, setFilteredProfiles] = useState<DiscoveryProfile[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isShaking, setIsShaking] = useState(false);
  const [loading, setLoading] = useState(true);

  // Filter states - now using the full filter object
  const [filters, setFilters] = useState<DiscoveryFilterValues>(DEFAULT_FILTERS);

  // Modal states
  const [showPhotos, setShowPhotos] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  // Modal states - removed showVideoGating as video is now optional

  const [showSuperPop, setShowSuperPop] = useState(false);

  // Interaction tracking
  const [likedProfiles, setLikedProfiles] = useState<Set<string>>(new Set());
  const [poppedProfiles, setPoppedProfiles] = useState<Set<string>>(new Set());
  const [watchedVideos, setWatchedVideos] = useState<Set<string>>(new Set()); // Track which profile videos were watched

  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const resumeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartXRef = useRef<number>(0);

  const currentProfile = filteredProfiles[currentIndex];
  const hasLocation = !!(userProfile?.latitude && userProfile?.longitude);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<DiscoveryFilterValues>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Fetch profiles from database with all filter-relevant data
  useEffect(() => {
    let isMounted = true;
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        console.warn("Profile fetch timeout - stopping loading");
        setLoading(false);
      }
    }, 5000); // 5 second timeout

    const fetchProfiles = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      
      try {
        // Simplified query first - try without join to see if that's the issue
        let query = supabase
          .from("profiles")
          .select("*")
          .neq("id", user.id)
          .eq("is_archived", false)
          .eq("is_disabled", false)
          .order("last_seen", { ascending: false, nullsFirst: false })
          .limit(50); // Reduced limit for faster loading

        // If scoped to a city, filter by city_id
        if (cityId) {
          query = query.eq("city_id", cityId);
        }

        // Fetch profiles - don't wait for userProfile, use it if available
        const { data, error } = await query;

        clearTimeout(timeoutId);

        if (!isMounted) return;

        if (error) {
          console.error("Error fetching profiles:", error);
          setProfiles([]);
        } else if (data && data.length > 0) {
          // Use userProfile coordinates if available, otherwise use null
          const userLat = userProfile?.latitude || null;
          const userLng = userProfile?.longitude || null;
          // Transform without cities join for now
          const transformed = data.map((p: any) => {
            const profile = transformDbProfile(p, userLat, userLng);
            // Get country_code from city_id if available (fallback)
            if (p.city_id && !profile.countryCode) {
              // We'll fetch this separately if needed, but for now just use what we have
            }
            return profile;
          });
          setProfiles(transformed);
        } else {
          console.log("No profiles found");
          setProfiles([]);
        }
      } catch (err) {
        console.error("Error in fetchProfiles:", err);
        clearTimeout(timeoutId);
        if (isMounted) {
          setProfiles([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProfiles();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [user, cityId]); // Removed userProfile dependency to start fetching immediately

  // Update profiles with user location when it becomes available
  // Note: We need to recalculate distances when user location becomes available
  // This is handled by re-fetching when userProfile changes, but we can optimize
  // by updating distances in-place if we store the raw DB profiles

  // Fetch user's liked and popped profiles
  useEffect(() => {
    const fetchInteractions = async () => {
      if (!user) return;

      const [likesRes, popsRes] = await Promise.all([
        supabase.from("likes").select("liked_id").eq("liker_id", user.id),
        supabase.from("pops").select("popped_id").eq("popper_id", user.id),
      ]);

      if (likesRes.data) {
        setLikedProfiles(new Set(likesRes.data.map((l) => l.liked_id)));
      }
      if (popsRes.data) {
        setPoppedProfiles(new Set(popsRes.data.map((p) => p.popped_id)));
      }
    };

    fetchInteractions();
  }, [user]);

  // Apply filters to profiles
  useEffect(() => {
    let result = [...profiles];

    // Verified only filter
    if (filters.showVerifiedOnly) {
      result = result.filter((p) => p.isVerified);
    }

    // Has video filter
    if (filters.hasVideo) {
      result = result.filter((p) => p.hasVideo);
    }

    // Religion filter
    if (filters.religion !== "any") {
      result = result.filter((p) => p.religion?.toLowerCase() === filters.religion);
    }

    // Social status filter
    if (filters.socialStatus !== "any") {
      result = result.filter((p) => p.socialStatus === filters.socialStatus);
    }

    // Gender filter
    if (filters.gender !== "everyone") {
      result = result.filter((p) => {
        if (filters.gender === "men") return p.gender === "male";
        if (filters.gender === "women") return p.gender === "female";
        return true;
      });
    }

    // Age range filter
    result = result.filter((p) => {
      if (!p.age) return true;
      return p.age >= filters.ageRange[0] && p.age <= filters.ageRange[1];
    });

    // Country filter
    if (filters.country !== "any") {
      result = result.filter((p) => p.countryCode === filters.country);
    }

    // Distance filter
    if (hasLocation) {
      result = result.filter((p) => {
        if (p.distanceNum === undefined) return true;
        return p.distanceNum <= filters.maxDistance;
      });
    }

    // Active recently filter (last 7 days)
    if (filters.activeRecently) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      result = result.filter((p) => {
        if (!p.lastSeen) return false;
        return new Date(p.lastSeen) >= sevenDaysAgo;
      });
    }

    // Gender-based video ranking: Males without video ranked lower (silent demotion)
    result.sort((a, b) => {
      // Profiles with videos always rank higher
      if (a.hasVideo && !b.hasVideo) return -1;
      if (!a.hasVideo && b.hasVideo) return 1;
      
      // Among no-video profiles, females rank higher than males
      if (!a.hasVideo && !b.hasVideo) {
        if (a.gender === "female" && b.gender === "male") return -1;
        if (a.gender === "male" && b.gender === "female") return 1;
      }
      
      return 0;
    });

    setFilteredProfiles(result);
    setCurrentIndex(0);
  }, [profiles, filters, hasLocation]);

  // Auto-advance carousel
  useEffect(() => {
    if (
      !isAutoPlaying ||
      showPhotos ||
      showProfile ||
      showVideo ||
      showMatch ||
      filteredProfiles.length === 0
    ) {
      return;
    }

    autoPlayTimerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % filteredProfiles.length);
    }, 5000);

    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current);
      }
    };
  }, [isAutoPlaying, filteredProfiles.length, showPhotos, showProfile, showVideo, showMatch]);

  // Pause auto-play on interaction
  const pauseAutoPlay = useCallback(() => {
    setIsAutoPlaying(false);

    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
    }

    resumeTimerRef.current = setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  }, []);

  // Navigate carousel
  const goToNext = useCallback(() => {
    if (filteredProfiles.length === 0) return;
    pauseAutoPlay();
    setCurrentIndex((prev) => (prev + 1) % filteredProfiles.length);
  }, [filteredProfiles.length, pauseAutoPlay]);

  const goToPrev = useCallback(() => {
    if (filteredProfiles.length === 0) return;
    pauseAutoPlay();
    setCurrentIndex((prev) => (prev - 1 + filteredProfiles.length) % filteredProfiles.length);
  }, [filteredProfiles.length, pauseAutoPlay]);

  // Touch/swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartXRef.current - touchEndX;

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          goToNext();
        } else {
          goToPrev();
        }
      }
    },
    [goToNext, goToPrev]
  );

  // Action handlers
  const handleLike = useCallback(async () => {
    if (!currentProfile || !user) return;
    pauseAutoPlay();

    if (likedProfiles.has(currentProfile.id)) {
      toast({ title: "Already Liked", description: `You've already liked ${currentProfile.name}` });
      return;
    }

    const insertData: any = {
      liker_id: user.id,
      liked_id: currentProfile.id,
      tab_context: tabContext,
    };

    if (cityId) insertData.city_id = cityId;
    if (teleportSessionId) insertData.teleport_session_id = teleportSessionId;

    const { error } = await supabase.from("likes").insert(insertData);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }

    setLikedProfiles((prev) => new Set(prev).add(currentProfile.id));
    toast({ title: "💕 Liked!", description: `You liked ${currentProfile.name}` });
    
    // Trigger interaction callback for nudge
    onInteraction?.("like");

    setTimeout(goToNext, 500);
  }, [currentProfile, user, likedProfiles, pauseAutoPlay, goToNext, tabContext, cityId, teleportSessionId, onInteraction]);

  // Pop action - requires video watch if profile has video, males need video to pop
  const handlePop = useCallback(async () => {
    if (!currentProfile || !user) return;
    pauseAutoPlay();

    // Male users without a video cannot pop (silent gate with gentle nudge)
    if (userProfile?.gender === "male" && !userProfile?.has_intro_video) {
      toast({ 
        title: "Add a video first", 
        description: "Upload an intro video to pop others' balloons ✨",
      });
      return;
    }

    // If target has a video and user hasn't watched it, prompt to watch first
    if (currentProfile.hasVideo && !watchedVideos.has(currentProfile.id)) {
      toast({ 
        title: "Watch video first", 
        description: `Watch ${currentProfile.name}'s intro video before popping!`,
      });
      setShowVideo(true);
      return;
    }

    if (poppedProfiles.has(currentProfile.id)) {
      toast({ title: "Already Popped", description: `You've already popped ${currentProfile.name}'s balloon` });
      return;
    }

    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);

    const insertData: any = {
      popper_id: user.id,
      popped_id: currentProfile.id,
      tab_context: tabContext,
    };

    if (cityId) insertData.city_id = cityId;
    if (teleportSessionId) insertData.teleport_session_id = teleportSessionId;

    const { error } = await supabase.from("pops").insert(insertData);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }

    setPoppedProfiles((prev) => new Set(prev).add(currentProfile.id));

    const { data: mutualPop } = await supabase
      .from("pops")
      .select("id")
      .eq("popper_id", currentProfile.id)
      .eq("popped_id", user.id)
      .maybeSingle();

    if (mutualPop) {
      // It's a match! Create match record in database
      const user1 = user.id < currentProfile.id ? user.id : currentProfile.id;
      const user2 = user.id < currentProfile.id ? currentProfile.id : user.id;
      
      await supabase.from("matches").insert({
        user1_id: user1,
        user2_id: user2,
        met_context: tabContext,
        match_source: "pop_balloon",
        source_context: cityId || teleportSessionId ? { city_id: cityId, teleport_session_id: teleportSessionId } : null,
      });
      
      onInteraction?.("match");
      setTimeout(() => {
        setShowMatch(true);
      }, 600);
    } else {
      // Just a pop
      onInteraction?.("pop");
      toast({ title: "🎈 Popped!", description: `Waiting for ${currentProfile.name} to pop back...` });
      setTimeout(goToNext, 800);
    }
  }, [currentProfile, user, userProfile, poppedProfiles, pauseAutoPlay, goToNext, tabContext, cityId, teleportSessionId, onInteraction, watchedVideos, setShowVideo]);

  const handleViewPhotos = useCallback(() => {
    pauseAutoPlay();
    setShowPhotos(true);
  }, [pauseAutoPlay]);

  const handleViewProfile = useCallback(() => {
    pauseAutoPlay();
    setShowProfile(true);
  }, [pauseAutoPlay]);

  const handleWatchVideo = useCallback(() => {
    pauseAutoPlay();
    setShowVideo(true);
    // Mark video as watched for pop eligibility
    if (currentProfile) {
      setWatchedVideos(prev => new Set(prev).add(currentProfile.id));
    }
  }, [pauseAutoPlay, currentProfile]);

  const handleReport = useCallback(async () => {
    setShowProfile(false);
    toast({ title: "Report Submitted", description: "Thank you for keeping our community safe." });
  }, []);

  const handleBlock = useCallback(async () => {
    setShowProfile(false);
    toast({ title: "User Blocked", description: `You won't see ${currentProfile?.name} anymore.` });
    goToNext();
  }, [currentProfile?.name, goToNext]);

  const handleStartChat = useCallback(() => {
    setShowMatch(false);
    toast({ title: "Chat Started! 💬", description: `Start your conversation with ${currentProfile?.name}` });
  }, [currentProfile?.name]);


  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  return {
    // State
    profiles,
    filteredProfiles,
    currentProfile,
    currentIndex,
    loading,
    isShaking,
    hasLocation,

    // Filters - new unified filter object
    filters,
    updateFilters,
    resetFilters,

    // Legacy filter props for backward compatibility
    maxDistance: filters.maxDistance,
    setMaxDistance: (v: number) => updateFilters({ maxDistance: v }),
    showVerifiedOnly: filters.showVerifiedOnly,
    setShowVerifiedOnly: (v: boolean) => updateFilters({ showVerifiedOnly: v }),

    // Modal states
    showPhotos,
    setShowPhotos,
    showProfile,
    setShowProfile,
    showVideo,
    setShowVideo,
    showMatch,
    setShowMatch,
    showSuperPop,
    setShowSuperPop,

    // Navigation
    setCurrentIndex,
    goToNext,
    goToPrev,
    pauseAutoPlay,

    // Touch handlers
    handleTouchStart,
    handleTouchEnd,

    // Action handlers
    handleLike,
    handlePop,
    handleViewPhotos,
    handleViewProfile,
    handleWatchVideo,
    handleReport,
    handleBlock,
    handleStartChat,

    // User profile
    userProfile,
  };
};
